// Season scorecard for The Forecast, derived only from published issue results.
// The pipeline's pregame `record` (scripts/lib/prediction-validation.cjs computeRecord) skips late
// editions; this view grades every published week and labels the late ones instead of hiding them.

const TEAMS_PER_WEEK = 10;

export function isLateEdition(issue) {
  return Boolean(issue?.editionType);
}

export function isGraded(issue) {
  const metrics = issue?.results?.metrics;
  return Boolean(metrics && Number.isFinite(metrics.correctPicks) && Number.isFinite(metrics.evaluatedPicks) && Number.isFinite(metrics.meanAbsoluteError));
}

function weekStatus(issue) {
  if (!isGraded(issue)) return 'pending';
  return isLateEdition(issue) ? 'final-late' : 'final';
}

export function summarizeGradedIssues(issues) {
  const totals = issues.filter(isGraded).reduce((total, issue) => {
    const { correctPicks, evaluatedPicks, tiedMatchups = 0, meanAbsoluteError } = issue.results.metrics;
    total.gradedWeeks += 1;
    total.correctPicks += correctPicks;
    total.evaluatedPicks += evaluatedPicks;
    total.tiedMatchups += tiedMatchups;
    total.absoluteErrorTotal += meanAbsoluteError * TEAMS_PER_WEEK;
    return total;
  }, { gradedWeeks: 0, correctPicks: 0, evaluatedPicks: 0, tiedMatchups: 0, absoluteErrorTotal: 0 });
  return {
    gradedWeeks: totals.gradedWeeks,
    correctPicks: totals.correctPicks,
    evaluatedPicks: totals.evaluatedPicks,
    tiedMatchups: totals.tiedMatchups,
    meanAbsoluteError: totals.gradedWeeks ? totals.absoluteErrorTotal / (totals.gradedWeeks * TEAMS_PER_WEEK) : null,
  };
}

export function buildSeasonScorecard(issues, season) {
  const seasonIssues = issues
    .filter((issue) => season == null || issue.season === season)
    .slice()
    .sort((a, b) => a.week - b.week);
  const rows = seasonIssues.map((issue) => {
    const graded = isGraded(issue);
    const metrics = graded ? issue.results.metrics : null;
    return {
      season: issue.season,
      week: issue.week,
      label: issue.label,
      status: weekStatus(issue),
      late: isLateEdition(issue),
      correctPicks: metrics ? metrics.correctPicks : null,
      evaluatedPicks: metrics ? metrics.evaluatedPicks : null,
      tiedMatchups: metrics ? metrics.tiedMatchups || 0 : null,
      meanAbsoluteError: metrics ? metrics.meanAbsoluteError : null,
    };
  });
  const lateGradedWeeks = seasonIssues.filter((issue) => isGraded(issue) && isLateEdition(issue)).map((issue) => issue.week);
  return {
    season: season ?? null,
    rows,
    totals: summarizeGradedIssues(seasonIssues),
    pregame: summarizeGradedIssues(seasonIssues.filter((issue) => !isLateEdition(issue))),
    lateGradedWeeks,
    pendingWeeks: rows.filter((row) => row.status === 'pending').map((row) => row.week),
  };
}

// Mirrors computeWeekMetrics: the higher final score wins, an exact tie grades neither way.
export function matchupOutcome(matchup, scores) {
  if (!matchup || !scores || !Array.isArray(matchup.teamIds) || matchup.teamIds.length !== 2) return null;
  const [a, b] = matchup.teamIds;
  const scoreA = Number(scores[a]);
  const scoreB = Number(scores[b]);
  if (scores[a] == null || scores[b] == null || !Number.isFinite(scoreA) || !Number.isFinite(scoreB)) return null;
  const difference = scoreA - scoreB;
  if (difference === 0) return { status: 'tie', winnerId: null, pickedId: matchup.winnerId, margin: 0 };
  const winnerId = difference > 0 ? a : b;
  return { status: winnerId === matchup.winnerId ? 'correct' : 'missed', winnerId, pickedId: matchup.winnerId, margin: Math.abs(difference) };
}

export function formatWeekList(weeks) {
  if (weeks.length === 0) return '';
  if (weeks.length === 1) return `Week ${weeks[0]}`;
  if (weeks.length === 2) return `Weeks ${weeks[0]} and ${weeks[1]}`;
  return `Weeks ${weeks.slice(0, -1).join(', ')}, and ${weeks[weeks.length - 1]}`;
}

export function formatPickRecord({ correctPicks, evaluatedPicks }) {
  return `${correctPicks}/${evaluatedPicks}`;
}

export function formatTies(tiedMatchups) {
  if (!tiedMatchups) return '';
  return `${tiedMatchups} tie${tiedMatchups === 1 ? '' : 's'} not counted`;
}
